/**
 * ActivitiesBalanceCard - Card KPI Premium de Atividades
 * Visual inspirado no MySellerProfileCard com header gradiente,
 * SmartAvatar com anel de progresso, badge de conclusão, cards de métricas
 * e mini-filtro de período integrado
 * 
 * @version 3.0.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial do card unificado
 * RELEASE: 2026-02-09 v2.0.0 - Visual premium com gradiente, SmartAvatar e badge
 * RELEASE: 2026-02-09 v3.0.0 - Integração com RPC, mini-filtro de período e comparativos
 */
import React from 'react';
import { Clock, CheckCircle2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { cn } from '@/lib/utils';
import type { PeriodFilter } from '@/hooks/useCrmOverviewFilters';

/**
 * Props do componente ActivitiesBalanceCard
 */
interface ActivitiesBalanceCardProps {
  /** Total de atividades no período */
  totalCount: number;
  /** Número de atividades pendentes (total, sem filtro de período) */
  pendingCount: number;
  /** Número de atividades concluídas no período */
  completedCount: number;
  /** Porcentagem de conclusão calculada pela RPC */
  completionPercentage: number;
  /** Variação de pendentes vs mês anterior (%) */
  pendingComparison: number;
  /** Variação de concluídas vs mês anterior (%) */
  completedComparison: number;
  /** Se está carregando dados */
  isLoading: boolean;
  /** URL do avatar do usuário atual */
  avatarUrl?: string | null;
  /** Nome do usuário (para fallback do avatar) */
  userName?: string | null;
  /** Email do usuário (opcional) */
  userEmail?: string | null;
  /** Filtro de período atual */
  periodFilter: PeriodFilter;
  /** Callback para mudança de preset de período */
  onPresetChange: (preset: PeriodFilter['preset']) => void;
  /** Callback para período customizado */
  onCustomChange: (startDate: Date, endDate: Date) => void;
}

/**
 * Componente de anel de progresso SVG ao redor do avatar
 * Tamanho ajustado para o SmartAvatar 3xl (96px)
 */
function ProgressRing({ progress, size = 96 }: { progress: number; size?: number }) {
  // Raio calculado para o anel ficar ao redor do avatar
  const radius = (size / 2) - 4;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <svg 
      className="absolute -inset-1" 
      width={size + 8} 
      height={size + 8} 
      viewBox={`0 0 ${size + 8} ${size + 8}`}
    >
      {/* Círculo de fundo (cinza) */}
      <circle
        cx={(size + 8) / 2}
        cy={(size + 8) / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        className="text-muted/30"
      />
      {/* Círculo de progresso (verde) */}
      <circle
        cx={(size + 8) / 2}
        cy={(size + 8) / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        className="text-emerald-500 transition-all duration-500 ease-out"
        style={{ 
          transform: 'rotate(-90deg)', 
          transformOrigin: '50% 50%' 
        }}
      />
    </svg>
  );
}

/**
 * Componente auxiliar para renderizar o indicador de comparação
 */
function ComparisonIndicator({ 
  value, 
  isPositiveGood = true 
}: { 
  value: number; 
  isPositiveGood?: boolean;
}) {
  // Para pendentes, negativo é bom (menos pendências); para concluídas, positivo é bom
  const isGood = isPositiveGood ? value > 0 : value < 0;
  const isBad = isPositiveGood ? value < 0 : value > 0;
  const isNeutral = value === 0;

  // Determina cor e ícone baseado no contexto
  let colorClass = 'text-muted-foreground';
  let Icon = Minus;

  if (isNeutral) {
    colorClass = 'text-muted-foreground';
    Icon = Minus;
  } else if (isGood) {
    colorClass = 'text-emerald-600 dark:text-emerald-400';
    Icon = TrendingUp;
  } else if (isBad) {
    colorClass = 'text-red-600 dark:text-red-400';
    Icon = TrendingDown;
  }

  // Formata o valor com sinal
  const formattedValue = value > 0 ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`;

  return (
    <div className={cn('flex items-center justify-center gap-0.5 mt-1', colorClass)}>
      <Icon className="h-3 w-3" />
      <span className="text-xs font-medium">{formattedValue}</span>
    </div>
  );
}

/**
 * Card KPI premium estilo "Balance Card"
 * Com mini-filtro de período integrado e comparativos mensais
 */
export function ActivitiesBalanceCard({
  totalCount,
  pendingCount,
  completedCount,
  completionPercentage,
  pendingComparison,
  completedComparison,
  isLoading,
  avatarUrl,
  userName,
  userEmail,
  periodFilter,
  onPresetChange,
  onCustomChange,
}: ActivitiesBalanceCardProps) {
  // Formata porcentagem para exibição
  const formattedPercentage = completionPercentage.toFixed(1);

  // Estado de loading
  if (isLoading) {
    return (
      <Card className="overflow-hidden">
        {/* Header gradiente skeleton */}
        <div className="h-16 bg-gradient-to-r from-primary/20 to-primary/5" />
        <CardContent className="-mt-10 flex flex-col items-center pb-6">
          {/* Skeleton do avatar */}
          <Skeleton className="h-24 w-24 rounded-full border-4 border-background" />
          {/* Skeleton do nome */}
          <Skeleton className="h-5 w-32 mt-4" />
          <Skeleton className="h-4 w-40 mt-2" />
          {/* Skeleton do badge */}
          <Skeleton className="h-6 w-28 mt-3 rounded-full" />
          {/* Skeleton do filtro */}
          <Skeleton className="h-8 w-full mt-4 rounded" />
          {/* Skeleton do total */}
          <div className="mt-4 w-full text-center p-3 rounded-lg bg-muted/50">
            <Skeleton className="h-4 w-24 mx-auto mb-2" />
            <Skeleton className="h-10 w-16 mx-auto" />
          </div>
          {/* Skeleton das métricas */}
          <div className="mt-4 w-full grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-muted/50">
              <Skeleton className="h-4 w-16 mx-auto mb-2" />
              <Skeleton className="h-6 w-10 mx-auto" />
              <Skeleton className="h-3 w-12 mx-auto mt-1" />
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <Skeleton className="h-4 w-16 mx-auto mb-2" />
              <Skeleton className="h-6 w-10 mx-auto" />
              <Skeleton className="h-3 w-12 mx-auto mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      {/* Header com gradiente premium */}
      <div className="h-16 bg-gradient-to-r from-primary/20 to-primary/5" />
      
      <CardContent className="-mt-10 flex flex-col items-center pb-6">
        {/* Avatar com anel de progresso */}
        <div className="relative">
          <ProgressRing progress={completionPercentage} size={96} />
          <SmartAvatar
            src={avatarUrl}
            name={userName}
            email={userEmail}
            size="3xl"
            className="border-4 border-background shadow-lg"
            use3DFallback={true}
          />
        </div>

        {/* Nome e email do usuário */}
        {userName && (
          <h3 className="mt-4 text-lg font-semibold text-foreground">
            {userName}
          </h3>
        )}
        {userEmail && (
          <p className="text-sm text-muted-foreground truncate max-w-full">
            {userEmail}
          </p>
        )}

        {/* Badge de taxa de conclusão */}
        <Badge variant="secondary" className="mt-3">
          {formattedPercentage}% de conclusão
        </Badge>

        {/* Mini-filtro de período - versão compacta */}
        <div className="mt-4 w-full">
          <CrmPeriodFilter
            value={periodFilter}
            onPresetChange={onPresetChange}
            onCustomChange={onCustomChange}
            className="w-full h-8 text-xs"
          />
        </div>

        {/* Card Total central */}
        <div className="mt-4 w-full text-center p-3 rounded-lg bg-muted/50">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
            Total Atividades
          </p>
          <p className="text-4xl font-bold text-foreground">
            {totalCount}
          </p>
        </div>

        {/* Cards de métricas lado a lado: Pendentes | Concluídas */}
        <div className="mt-4 w-full grid grid-cols-2 gap-3">
          {/* Card Pendentes */}
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <div className="h-6 w-6 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Clock className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">
                Pendentes
              </span>
            </div>
            <p className={cn(
              "text-2xl font-bold",
              pendingCount > 0 
                ? "text-orange-600 dark:text-orange-400" 
                : "text-foreground"
            )}>
              {pendingCount}
            </p>
            {/* Comparativo: para pendentes, negativo é bom (menos pendências) */}
            <ComparisonIndicator value={pendingComparison} isPositiveGood={false} />
          </div>

          {/* Card Concluídas */}
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <div className="h-6 w-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">
                Concluídas
              </span>
            </div>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {completedCount}
            </p>
            {/* Comparativo: para concluídas, positivo é bom (mais conclusões) */}
            <ComparisonIndicator value={completedComparison} isPositiveGood={true} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
